import { useEffect, useMemo, useRef, useState } from 'react';

const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
let googleMaps3dLoader;

function getMapQuery({ geo, address, label }) {
  if (geo?.lat && geo?.lng) return `${geo.lat},${geo.lng}`;
  return [address, label].filter(Boolean).join(' ');
}

function buildGoogleMapEmbedUrl(query, mode) {
  const params = new URLSearchParams({
    q: query,
    z: mode === 'satellite' ? '15' : '13',
    output: 'embed',
  });
  if (mode === 'satellite') params.set('t', 'k');
  return `https://www.google.com/maps?${params.toString()}`;
}

function buildGoogleMapUrl(query, mapUrl) {
  return mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function buildGoogleEarthUrl(query) {
  return `https://earth.google.com/web/search/${encodeURIComponent(query)}`;
}

function loadGoogleMaps3d() {
  if (!googleMapsApiKey) return Promise.reject(new Error('Google Maps API key is not configured.'));
  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary('maps3d');
  }
  if (googleMaps3dLoader) return googleMaps3dLoader;

  googleMaps3dLoader = new Promise((resolve, reject) => {
    const callbackName = '__drsFarmGoogleMaps3dLoaded';
    const existingScript = document.getElementById('google-maps-3d-script');

    window[callbackName] = async () => {
      try {
        resolve(await window.google.maps.importLibrary('maps3d'));
      } catch (error) {
        reject(error);
      }
    };

    if (existingScript) return;

    const script = document.createElement('script');
    script.id = 'google-maps-3d-script';
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Google Maps 3D script failed to load.'));
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&v=beta&libraries=maps3d&loading=async&callback=${callbackName}`;
    document.head.appendChild(script);
  });

  return googleMaps3dLoader;
}

function FarmMap3D({ geo, label, camera = {} }) {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!geo?.lat || !geo?.lng || !hostRef.current) return undefined;

    let mapElement;
    let cancelled = false;
    const host = hostRef.current;
    host.replaceChildren();
    setFailed(false);

    loadGoogleMaps3d()
      .then(({ Map3DElement, Marker3DElement }) => {
        if (cancelled) return;
        mapElement = new Map3DElement({
          center: {
            lat: camera.center?.lat ?? geo.lat,
            lng: camera.center?.lng ?? geo.lng,
            altitude: camera.altitude ?? 70,
          },
          mode: 'SATELLITE',
          tilt: camera.tilt ?? 65,
          heading: camera.heading ?? 24,
          range: camera.range ?? 420,
          defaultUIHidden: false,
          gestureHandling: 'COOPERATIVE',
          description: `${label || '圃場'}の3D栽培環境`,
        });
        mapElement.className = 'map-3d-root';
        if (Marker3DElement) {
          const marker = new Marker3DElement({
            position: { lat: geo.lat, lng: geo.lng, altitude: 0 },
            label: label || '圃場',
            sizePreserved: true,
            zIndex: 10,
          });
          mapElement.append(marker);
        }
        host.appendChild(mapElement);
      })
      .catch((error) => {
        console.error('Google Maps 3D failed to load', error);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (mapElement?.remove) mapElement.remove();
      host.replaceChildren();
    };
  }, [camera.altitude, camera.center?.lat, camera.center?.lng, camera.heading, camera.range, camera.tilt, geo?.lat, geo?.lng, label]);

  return (
    <div className="map-3d-shell">
      <div ref={hostRef} className="map-3d-host" aria-label={`${label || '圃場'}の3D地図`} />
      {failed ? <p className="map-fallback-note">3D表示を読み込めませんでした。航空写真に切り替えて確認できます。</p> : null}
    </div>
  );
}

export default function FarmMap({ geo, label, address, mapUrl, camera }) {
  const canRender3d = Boolean(googleMapsApiKey && geo?.lat && geo?.lng);
  const [mode, setMode] = useState(canRender3d ? '3d' : 'satellite');
  const query = useMemo(() => getMapQuery({ geo, address, label }), [address, geo, label]);

  useEffect(() => {
    if (canRender3d) {
      setMode((currentMode) => (currentMode === 'satellite' || currentMode === 'map' ? currentMode : '3d'));
    } else if (mode === '3d') {
      setMode('satellite');
    }
  }, [canRender3d, mode]);

  if (!query) {
    return <p>位置情報が未登録です。</p>;
  }

  return (
    <div className="farm-map">
      <div className="farm-map-toolbar" aria-label="地図表示切り替え">
        <div className="segmented-control">
          {canRender3d ? (
            <button
              type="button"
              className={mode === '3d' ? 'is-active' : ''}
              onClick={() => setMode('3d')}
              aria-pressed={mode === '3d'}
            >
              3D
            </button>
          ) : null}
          <button
            type="button"
            className={mode === 'satellite' ? 'is-active' : ''}
            onClick={() => setMode('satellite')}
            aria-pressed={mode === 'satellite'}
          >
            航空写真
          </button>
        </div>
      </div>
      {mode === '3d' && canRender3d ? (
        <FarmMap3D geo={geo} label={label} camera={camera} />
      ) : (
        <iframe
          className="map-root"
          title={`${label || '圃場'}の航空写真`}
          src={buildGoogleMapEmbedUrl(query, 'satellite')}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      )}
      <div className="farm-map-actions">
        <a className="text-btn" href={buildGoogleMapUrl(query, mapUrl)} target="_blank" rel="noreferrer">Google Mapsで開く</a>
        <a className="text-btn" href={buildGoogleEarthUrl(query)} target="_blank" rel="noreferrer">Google Earthで見る</a>
      </div>
    </div>
  );
}
